import KeyValuePair from '@/modules/core/support/KeyValuePair'

/**
 * Elemento de la política de autorización
 *
 * Define las propiedades comunes de los permisos y roles. Estos elementos
 * definen la política de autorización de la aplicación
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export abstract class AuthorizationItem {
    /**
     * Identificador único del elemento, asignado por el servidor.
     *
     * @var { number }
     */
    public id = 0

    /**
     * Clave de identificación
     *
     * Cadena alfanumérica, sin espacios ni caracteres especiales, asignada por el usuario.
     * No puede repetirse a nivel de nodo padre, es decir, no puede existir ningún
     * elemento hermano con la misma clave
     *
     * @var { string }
     */
    public key = ''

    /**
     * Título del elemento
     *
     * Nombre descriptivo y breve del elemento
     *
     * @var { string }
     */
    public title = ''

    /**
     * Descripción del elemento
     *
     * Texto opcional aunque recomendado para explicar la función u objetivo
     * del elemento.
     *
     * @var { string }
     */
    public description = ''

    protected _path = ''

    /**
     * Elementos hijos
     */
    public children: AuthorizationItem[] = []

    public createdAt = new Date()
    public updatedAt = new Date()
    public updatedPath = ''


    /**
     * Indica si el elemento es nuevo y todavía no ha sido registrado en el servidor.
     */
    public get isNew () {
        return !this.id
    }

    /**
     * Establece la ruta de acceso al permiso
     *
     * @param value
     */
    public set path (value: string) {
        this._path = value
        this.updatedPath = value
    }

    /**
     * Ruta de acceso al permiso
     */
    public get path () {
        return this.isNew ? `${this._path}/${this.key}` : this._path
    }

    /**
     * Obtiene la ruta del permiso padre
     */
    public get parentPath () {
        const segments = this.path.split('/')
        if (segments.length === 1) {
            return ''
        }
        segments.pop()

        return segments.join('/')
    }

    /**
     * Retorna la lista de rutas de los permisos descendientes en un mismo nivel
     */
    public get flattenedPaths (): string[] {
        const paths = [this.path]
        this.children.forEach(c => {
            paths.push(...c.flattenedPaths)
        })

        return paths
    }

    /**
     * Busca un elemento por su ruta y lo elimina
     *
     * @param path
     */
    public remove (path: string) {
        const index = this.children.findIndex(c => c.path === path)
        if (index !== - 1) {
            this.children.splice(index, 1)

            return
        }

        this.children.forEach(c => c.remove(path))
    }

    /**
     * Retorna el numero total de elementos descendientes (hijos y ancestros)
     */
    public get totalItems (): number {
        const childrenCount = this.children.map(c => c.totalItems)
        if (!childrenCount) {
            return 0
        }

        return this.children.length + childrenCount.reduce((o, c) => o + c, 0)
    }

    /**
     * Busca de forma recursiva un elemento por su ruta de acceso
     *
     * @param path
     */
    public find (path: string): AuthorizationItem | null {
        if (this.path === path) {
            return this
        }

        for (const child of this.children) {
            const result = child.find(path)
            if (result) {
                return result
            }
        }

        return null
    }

    /**
     * Busca de forma recursiva un elemento por su ruta de acceso
     *
     * @param path
     */
    public findByPath (path: string): AuthorizationItem | null {
        if (this.path === path) {
            return this
        }

        for (const child of this.children) {
            const result = child.findByPath(path)
            if (result) {
                return result
            }
        }

        return null
    }

    /**
     * Busca de forma recursiva ocurrencias por nombre, clave o descripción en
     * los elementos hijos y todos sus descendientes
     */
    public findOccurrences (search: string): AuthorizationItem[] {
        let result: AuthorizationItem[] = []
        // Indica si el título, descripción o clave incluyen un fragmento del texto
        // argumentado
        const contains = (p: AuthorizationItem, search: string) =>
            p.title.includes(search)
            || p.description.includes(search)
            || p.key.includes(search)

        if (contains(this, search)) {
            result.push(this)
        }

        this.children.forEach(child => {
            result = result.concat(child.findOccurrences(search))
        })

        return result
    }

    /**
     * Añade un permiso hijo
     *
     * @param child
     */
    public addChild (child: AuthorizationItem) {
        const index = this.children.findIndex(c => c.key === child.key)
        // Si ya existe un permiso con la misma clave, se sustituirá por el mas nuevo
        index === -1
            ? this.children.push(child)
            : this.children.splice(index, 1, child)

        return this
    }

    /**
     * Elimina un elemento hijo
     *
     * @param path
     */
    public removeChild (path: string) {
        const index = this.children.findIndex(c => c.path === path)
        if (index !== - 1) {
            this.children.splice(index, 1)

            return
        }

        this.children.forEach(c => c.removeChild(path))
    }

    /**
     * Convierte la entidad en un objeto plano listo para ser enviado a
     * la api de la aplicación
     *
     * @return { KeyValuePair }
     */
    public abstract serialize (): KeyValuePair;
}
